import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type { ScoreResultPayload } from './scoreResultUrl';
import { isLeadPlumbingConfigured, mailtoAuditFallback, submitLead } from '../lib/leads';
import { trackAuditRequest } from '../lib/analytics';
type AuditRequestFormProps = {
  score: ScoreResultPayload;
  helperText: string;
};
export function AuditRequestForm({
  score,
  helperText
}: AuditRequestFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const plumbingReady = isLeadPlumbingConfigured();
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.');
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setError(null);
    const result = await submitLead({
      name: name.trim(),
      email: email.trim(),
      note: note.trim() || undefined,
      source: 'audit_request',
      score
    });
    if (result.ok) {
      trackAuditRequest('form');
      setStatus('success');
      return;
    }

    // Fall back to mailto when plumbing is missing or failed.
    trackAuditRequest('mailto_fallback');
    window.location.href = mailtoAuditFallback(score);
    setStatus(result.mode === 'none' ? 'idle' : 'error');
    if (result.mode === 'error') {
      setError(result.error || 'Could not save your request. Opening email as a fallback.');
    }
  };
  if (status === 'success') {
    return <div className="rounded-3xl bg-navy px-7 py-10 text-center text-paper md:px-12" data-id="element-36" data-mp="2f0m6c55gnmrq">
        <h2 className="font-serif text-3xl leading-tight md:text-4xl" data-id="element-37" data-mp="3lgf07sbk2err">
          Request received.
        </h2>
        <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-paper/75" data-id="element-38" data-mp="3qeq8vzq46j8e">
          Thanks{name ? `, ${name.split(' ')[0]}` : ''}. We’ll follow up about a
          Reader Path Audit using the score you just completed.
        </p>
      </div>;
  }
  return <section className="rounded-3xl bg-navy px-7 py-10 text-center text-paper md:px-12" aria-labelledby="audit-heading" data-id="element-39" data-mp="2vrrcr0xe6vq8">
      <h2 id="audit-heading" className="font-serif text-3xl leading-tight md:text-4xl" data-id="element-40" data-mp="35q77rezjip50">
        See what a clearer path could unlock.
      </h2>
      <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-paper/75" data-id="element-41" data-mp="2i1nx4st0q09r">
        {helperText}
      </p>

      <form onSubmit={onSubmit} className="mx-auto mt-8 max-w-md space-y-3 text-left" data-id="element-42" data-mp="2d2uiuy5wq58j">
        <label className="block" data-id="element-43" data-mp="2rrh9hw0zijf2">
          <span className="sr-only" data-id="element-44" data-mp="2rsimw75h3hzy">Name</span>
          <input type="text" name="name" autoComplete="name" required placeholder="Your name" value={name} onChange={e => setName(e.target.value)} className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-paper placeholder:text-paper/45 focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-45" data-mp="2mz62yabqnzdj" />
        </label>
        <label className="block" data-id="element-46" data-mp="9s7tg7qn0trf">
          <span className="sr-only" data-id="element-47" data-mp="9r6cxnqurf2b">Email</span>
          <input type="email" name="email" autoComplete="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-paper placeholder:text-paper/45 focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-48" data-mp="ekretqvkgt6s" />
        </label>
        <label className="block" data-id="element-49" data-mp="skaa3j3a4a1o">
          <span className="sr-only" data-id="element-50" data-mp="xik9y6zy7ytm">Optional note</span>
          <textarea name="note" rows={3} placeholder="Optional note (timeline, book title, etc.)" value={note} onChange={e => setNote(e.target.value)} className="w-full resize-y rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-paper placeholder:text-paper/45 focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-51" data-mp="xi24cjrec891" />
        </label>

        {error && <p className="text-sm text-sand" role="alert" data-id="element-52" data-mp="38ad8zewrs1hv">
            {error}
          </p>}

        <button type="submit" disabled={status === 'submitting'} className="inline-flex min-h-[56px] w-full items-center justify-center rounded-xl bg-[#d4a574] px-8 py-4 font-bold text-white transition-colors hover:bg-[#c89860] focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-60" data-id="element-53" data-mp="3ohhuaruy5vxf">
          {status === 'submitting' ? 'Sending…' : plumbingReady ? 'Request a Reader Path Audit' : 'Request a Reader Path Audit'}
          <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" data-id="element-54" data-mp="ba6nqk04s4a6" />
        </button>
      </form>

      <p className="mt-4 text-sm text-paper/55" data-id="element-55" data-mp="6bvas0ejhdnq">
        {plumbingReady ? 'We’ll email you back. Your score details are included automatically.' : 'Lead storage isn’t configured yet — this will open an email draft as a fallback.'}
      </p>

      <a href={mailtoAuditFallback(score)} onClick={() => trackAuditRequest('mailto_fallback')} className="mt-3 inline-block text-sm font-medium text-seaglass-light underline underline-offset-4 hover:text-paper" data-id="element-56" data-mp="3tixgin8v28he">
        Prefer email instead
      </a>
    </section>;
}